import React, { useEffect, useMemo, useState } from "react";
import { CARD_LABEL, META_SIZE, ROW_SIZE } from "../utils/homeType";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useWeeklyAwards } from "../hooks/useWeeklyAwards";
import { buildGamesBySlug } from "../utils/gameStatus";
import { formatGameLine, latestScoredWeek, seasonThroughWeek } from "../utils/topTeams";
import { fetchWeekGames } from "../utils/weekGames";
import LineupBanner from "./LineupBanner";
import TeamLogoImage from "./league/TeamLogoImage";

// Manager and Muppet of the Week: the best and worst manager week across every
// real league, for the last week that has finished, with the five starters
// that produced each.
//
// Finished weeks only, the same boundary as the Top 25's season totals
// (seasonThroughWeek): a week in progress would crown a lineup whose games
// are still being played. Both the awards and the week's games are cached to
// disk for a day, and the games are the same cache entry the Top 25 reads, so
// this card usually costs one call a day.
//
// A starter's points come from the lineup's team_points, which already carry
// the captain's 2x and the 5x Play, so they add up to the total shown.

const ROW_TEXT = { fontSize: ROW_SIZE, fontWeight: 600, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" };
const SUB_TEXT = { fontSize: META_SIZE, color: "var(--color-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

const pointsColor = (points) => (points < 0 ? "var(--color-danger-text)" : "var(--color-text-strong)");

function Badge({ children, color, background }) {
  return (
    <span style={{ fontSize: 9, fontWeight: 800, color, backgroundColor: background, borderRadius: 4, padding: "1px 4px", flexShrink: 0 }}>
      {children}
    </span>
  );
}

function StarterRow({ slug, award, teams, gamesBySlug }) {
  const team = teams[slug];
  const name = team?.school || slug;
  const points = award.team_points?.[slug];
  const isCaptain = award.captain === slug;
  const isTripPlay = award.trip_play_team === slug;
  const gameLine = formatGameLine(gamesBySlug[slug], teams) || "No game";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderTop: "1px solid var(--color-bg-subtle)" }}>
      <TeamLogoImage teamName={name} primaryColor={team?.colors?.primary} size={24} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          <span style={{ ...ROW_TEXT, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{name}</span>
          {isTripPlay ? (
            <Badge color="var(--color-tripplay-text)" background="var(--color-tripplay-fill)">5x</Badge>
          ) : isCaptain ? (
            <Badge color="var(--color-captain-text)" background="var(--color-captain-fill)">C</Badge>
          ) : null}
        </div>
        <div style={SUB_TEXT}>{gameLine}</div>
      </div>
      <span style={{ ...ROW_TEXT, color: pointsColor(points ?? 0), flexShrink: 0 }}>
        {typeof points === "number" ? points : "0"}
      </span>
    </div>
  );
}

// The header is the button: tapping anywhere on the award opens its five
// starters. Closed by default, so the card reads as two results first.
function AwardSection({ label, emoji, award, teams, gamesBySlug }) {
  const [open, setOpen] = useState(false);
  const starters = (award.starters || []).filter(Boolean);
  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <div style={{ borderTop: "1px solid var(--color-bg-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{ display: "block", width: "100%", textAlign: "left", padding: "12px 14px 10px", backgroundColor: "var(--color-bg-page)", border: "none", cursor: "pointer", font: "inherit" }}
      >
        <div style={{ ...CARD_LABEL, marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...ROW_TEXT, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {award.team_name} <span aria-hidden="true">{emoji}</span>
          </span>
          <span style={{ ...ROW_TEXT, color: pointsColor(award.points), flexShrink: 0 }}>
            {Number(award.points)} pts
          </span>
          <Chevron size={16} color="var(--color-text-faint)" style={{ flexShrink: 0 }} aria-hidden="true" />
        </div>
        <div style={SUB_TEXT}>{award.first_name} in {award.league_name}</div>
      </button>
      {open && starters.map((slug) => (
        <StarterRow key={slug} slug={slug} award={award} teams={teams} gamesBySlug={gamesBySlug} />
      ))}
    </div>
  );
}

function WeeklyAwardsCard() {
  const { teams, seasonConfig } = useAuth();
  const currentWeek = seasonConfig?.currentWeek;
  const week = useMemo(
    () => seasonThroughWeek(currentWeek, latestScoredWeek(teams)),
    [currentWeek, teams]
  );
  const { best, worst, loading } = useWeeklyAwards(week);

  const [gamesBySlug, setGamesBySlug] = useState({});
  useEffect(() => {
    if (!week) return undefined;
    let cancelled = false;
    fetchWeekGames(week, { isFinished: true })
      .then((rows) => { if (!cancelled) setGamesBySlug(buildGamesBySlug(rows)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [week]);

  // No finished week, or nothing scored in it: no card rather than an empty one.
  if (!week || loading || (!best && !worst)) return null;

  return (
    <div style={{ marginTop: 32, backgroundColor: "var(--color-bg-card)", border: "1.5px solid var(--color-bg-subtle)", borderRadius: 16, overflow: "hidden" }}>
      <LineupBanner title="Weekly Awards" />
      <div style={{ padding: "10px 14px", fontSize: 12, fontWeight: 600, color: "var(--color-text-muted)" }}>
        Week {week}, Across Every League
      </div>
      {/* Named rather than described: "Best Week" and "Worst Week" stated the
          measurement, which the points column already does. The banner above
          carries the Lineup CFB name, so the labels do not repeat it. */}
      {best && <AwardSection label="Manager of the Week" emoji="⭐" award={best} teams={teams} gamesBySlug={gamesBySlug} />}
      {worst && <AwardSection label="Muppet of the Week" emoji="💩" award={worst} teams={teams} gamesBySlug={gamesBySlug} />}
    </div>
  );
}

export default WeeklyAwardsCard;
